import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import useSWR from 'swr';
import { AlertCircle, CheckCircle2, Mic2, Settings2, UserCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { conclaveApi } from '@/lib/conclave-api';
import {
  hasAnyNetworkingDetail,
  parseCategoryOptions,
  parseDreamReferralList,
} from '@/lib/conclave-networking';
import { conclaveSubPath } from '@/lib/conclave-paths';

type CardTone = 'live' | 'todo' | 'done' | 'info';

type CardContentState = {
  tone: CardTone;
  icon: typeof Mic2;
  title: string;
  description: string;
  primary?: { label: string; to: string; variant?: 'default' | 'outline' };
};

const TONE_CLASS: Record<CardTone, string> = {
  live: 'border-primary/40 bg-primary/10',
  todo: 'border-amber-500/40 bg-amber-500/10',
  done: 'border-emerald-500/30 bg-emerald-500/5',
  info: 'border-border bg-card',
};

const TONE_ICON_CLASS: Record<CardTone, string> = {
  live: 'text-primary',
  todo: 'text-amber-600',
  done: 'text-emerald-600',
  info: 'text-muted-foreground',
};

export function ConclaveExperienceCard({
  isRegistered,
  isAdmin,
  communityId,
  eventId: eventIdProp,
  eventSlug,
}: {
  isRegistered: boolean;
  isAdmin: boolean;
  communityId?: string | null;
  /** Canonical event id for API calls */
  eventId?: string;
  /** Prefer slug in UI paths when available */
  eventSlug?: string | null;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const skipLiveRedirect =
    (location.state as { skipLiveRedirect?: boolean } | null)?.skipLiveRedirect === true;
  const redirectedRef = useRef(false);
  const { eventId: routeEventId = '', communityId: routeCommunityId } = useParams();
  const resolvedCommunityId = communityId ?? routeCommunityId ?? null;
  const apiEventId = eventIdProp || routeEventId;
  const pathEventId = eventSlug || eventIdProp || routeEventId;
  const profilePath = conclaveSubPath(pathEventId, 'profile', resolvedCommunityId);
  const livePath = conclaveSubPath(pathEventId, 'live', resolvedCommunityId);
  const organisePath = conclaveSubPath(pathEventId, 'organise', resolvedCommunityId);

  const { data, isLoading, error } = useSWR(
    isRegistered && apiEventId ? `conclave-profile-status-${apiEventId}` : null,
    () => conclaveApi.getProfile(apiEventId)
  );

  const { data: tableData } = useSWR(
    isRegistered && apiEventId ? `conclave-my-table-${apiEventId}` : null,
    () => conclaveApi.myTable(apiEventId),
    { refreshInterval: 15_000 }
  );

  const { data: giveAskData } = useSWR(
    isRegistered && apiEventId ? `conclave-event-gives-asks-${apiEventId}` : null,
    () => conclaveApi.listEventGiveAsks(apiEventId)
  );
  const enabledItems = (giveAskData?.items ?? []).filter((item) => item.enabled);
  const enabledGives = enabledItems.filter((item) => item.type === 'GIVE').length;
  const enabledAsks = enabledItems.filter((item) => item.type === 'ASK').length;
  const profile = data?.profile as Record<string, unknown> | null | undefined;
  const profileComplete =
    Boolean(profile) &&
    enabledGives >= 3 &&
    enabledAsks >= 3 &&
    parseDreamReferralList(profile?.dreamReferral).some(hasAnyNetworkingDetail) &&
    parseCategoryOptions(profile?.connectCategories).length >= 3 &&
    parseCategoryOptions(profile?.giveBusinessCategories).length >= 3;
  const profileForbidden =
    Boolean(error) && (error as { response?: { status?: number } })?.response?.status === 403;

  const session = tableData?.session as { id?: string; index?: number; status?: string } | null;
  const assignment = tableData?.assignment as { tableNumber?: number } | null;
  const sessionActive = session?.status === 'ACTIVE';
  const tableNumber = assignment?.tableNumber ?? null;

  useEffect(() => {
    if (!isRegistered || isAdmin || !sessionActive || skipLiveRedirect || redirectedRef.current) {
      return;
    }
    redirectedRef.current = true;
    navigate(livePath, { replace: true });
  }, [isRegistered, isAdmin, sessionActive, skipLiveRedirect, livePath, navigate]);

  if (!isRegistered && !isAdmin) return null;

  const state: CardContentState = (() => {
    if (!isRegistered) {
      return {
        tone: 'info',
        icon: AlertCircle,
        title: 'Taking part in the conclave?',
        description:
          'Register for a ticket first. Then you can fill your networking profile and join the tables.',
      };
    }
    if (profileForbidden) {
      return {
        tone: 'todo',
        icon: AlertCircle,
        title: 'Registration needed',
        description: 'Register with your own ticket (not only as booked-by) to join the conclave.',
      };
    }
    if (sessionActive) {
      return {
        tone: 'live',
        icon: Mic2,
        title: 'Live session is on',
        description: [
          tableNumber != null
            ? `Session ${(session?.index ?? 0) + 1} · you are at Table ${tableNumber}`
            : `Session ${(session?.index ?? 0) + 1} · your table appears once seating is ready`,
          data && !profileComplete
            ? 'Your networking profile is incomplete, so you may not get a table.'
            : null,
        ]
          .filter(Boolean)
          .join(' '),
        primary: { label: 'Join live session', to: livePath },
      };
    }
    if (isLoading) {
      return { tone: 'info', icon: UserCircle, title: 'Checking your profile…', description: '' };
    }
    if (!profileComplete) {
      return {
        tone: 'todo',
        icon: UserCircle,
        title: 'Complete your networking profile',
        description: 'Add your gives, asks and categories so we can seat you at a table.',
        primary: { label: 'Complete profile', to: profilePath },
      };
    }
    if (tableNumber != null) {
      return {
        tone: 'done',
        icon: CheckCircle2,
        title: `You are at Table ${tableNumber}`,
        description: `Live opens for you automatically when the organiser starts session ${(session?.index ?? 0) + 1}.`,
        primary: { label: 'Edit profile', to: profilePath, variant: 'outline' },
      };
    }
    return {
      tone: 'done',
      icon: CheckCircle2,
      title: "You're all set",
      description: session
        ? 'Your profile is ready. Table arrangement will show here once the organiser generates seating.'
        : "Your profile is ready. The organiser hasn't set up the sessions yet.",
      primary: { label: 'Edit profile', to: profilePath, variant: 'outline' },
    };
  })();

  const Icon = state.icon;
  const canOpenLive = !profileForbidden && (isRegistered || isAdmin);

  return (
    <Card className={TONE_CLASS[state.tone]}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className={`h-5 w-5 ${TONE_ICON_CLASS[state.tone]}`} />
          {state.title}
        </CardTitle>
        {state.description ? <CardDescription>{state.description}</CardDescription> : null}
      </CardHeader>
      {state.primary || isRegistered || isAdmin ? (
        <CardContent className="flex flex-col gap-2">
          {state.primary ? (
            <Button
              asChild
              className="w-full rounded-xl"
              variant={state.primary.variant ?? 'default'}
            >
              <Link to={state.primary.to}>{state.primary.label}</Link>
            </Button>
          ) : null}
          {canOpenLive && state.primary?.to !== livePath ? (
            <Button asChild variant="outline" className="w-full rounded-xl">
              <Link to={livePath}>
                <Mic2 className="mr-2 h-4 w-4" />
                Live session
              </Link>
            </Button>
          ) : null}
          {isRegistered && state.primary?.to !== profilePath ? (
            <Button asChild variant="outline" className="w-full rounded-xl">
              <Link to={profilePath}>
                <UserCircle className="mr-2 h-4 w-4" />
                Edit networking profile
              </Link>
            </Button>
          ) : null}
          {isAdmin ? (
            <Button asChild variant="ghost" size="sm" className="rounded-lg">
              <Link to={organisePath}>
                <Settings2 className="mr-2 h-4 w-4" />
                Organiser tools
              </Link>
            </Button>
          ) : null}
        </CardContent>
      ) : null}
    </Card>
  );
}
